import React, { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import api, { setToken } from "../../../../api/api";
import history from "../../../../history";
import { userLogin } from "../../../actions";

import UserBill from "../UserBill";
import UserNote from "../UserNote";
import UserFileHistoryModal from "./UserFileHistoryModal";
import UserLessonMarksModal from "./UserLessonMarksModal";
import UserLessonHistoryModal from "./UserLessonHistoryModal";
import UserErrorReportsModal from "./UserErrorReportsModal";
import UserContactMessagesModal from "./UserContactMessagesModal";

// New modular modals
import EditProfileModal from "./EditProfileModal";
import ChangePasswordModal from "./ChangePasswordModal";
import ChangeLoginInfoModal from "./ChangeLoginInfoModal";
import ChangeSchoolModal from "./ChangeSchoolModal";
import MoveClassRoomModal from "./MoveClassRoomModal";
import MoreInfoModal from "./MoreInfoModal";
import SchoolModals from "../../Schools/components/SchoolModals";
import configCountries from "../../../Config/countries.json";
import { LazyLoadImage } from "react-lazy-load-image-component";

const UserModals = ({
    activeModal: propActiveModal,
    activeSubModal: propActiveSubModal,
    activeUser: propActiveUser,
    onClose,
    onSubmitEdit: propSubmitEdit,
    onSubmitPassword: propSubmitPassword,
    onSubmitLoginInfo: propSubmitLoginInfo,
    onSubmitChangeSchool: propSubmitChangeSchool,
    onSubmitChangeClassRoom: propSubmitChangeClassRoom,
    promiseSchoolOptions: propPromiseSchoolOptions,
    promiseClassRoomOptions: propPromiseClassRoomOptions,
    onAction: propOnAction,
    schoolModalOpen,
    selectedSchool,
    schoolUsers,
    loadingSchoolUsers,
    onCloseSchoolModal,
    onSuccess,
    t
}) => {
    const dispatch = useDispatch();

    const [internalActiveModal, setInternalActiveModal] = useState(null);
    const [internalActiveSubModal, setInternalActiveSubModal] = useState(null);
    const [internalActiveUser, setInternalActiveUser] = useState(null);

    useEffect(() => {
        setInternalActiveUser(propActiveUser);
    }, [propActiveUser]);

    useEffect(() => {
        if (propActiveModal) {
            setInternalActiveModal(propActiveModal);
        } else if (propActiveUser) {
            setInternalActiveModal("moreInfo");
        } else {
            setInternalActiveModal(null);
        }
    }, [propActiveModal, propActiveUser]);

    useEffect(() => {
        setInternalActiveSubModal(propActiveSubModal);
    }, [propActiveSubModal]);

    const activeUser = internalActiveUser;
    const activeModal = internalActiveModal;
    const activeSubModal = internalActiveSubModal;

    const [selectedUser, setSelectedUser] = useState(activeUser);

    useEffect(() => {
        setSelectedUser(activeUser);
    }, [activeUser]);

    // Handle closing modals
    const handleClose = useCallback(() => {
        if (activeSubModal) {
            setInternalActiveSubModal(null);
        } else {
            setInternalActiveModal(null);
            setInternalActiveUser(null);
            if (onClose) onClose();
        }
    }, [activeSubModal, onClose]);

    // handle administrative action triggers
    const handleActionTrigger = useCallback((actionType, item) => {
        if (propOnAction) {
            propOnAction(actionType, item);
            return;
        }

        const confirmAndExecute = (msg, apiCall, successIcon, successTitleKey, successBodyKey) => {
            const _toastId = toast(t("Processing"), { position: "bottom-left", autoClose: false });
            toast.update(_toastId, {
                render: () => (
                    <div style={{ direction: "rtl", textAlign: "right" }}>
                        <i className="fa-spin fas fa-spinner"></i> {msg}
                        <div className="row row-cols-2 mt-3">
                            <div className="col">
                                <button type="button" className="btn btn-danger btn-sm btn-block" onClick={() => toast.dismiss(_toastId)}>
                                    <i className="icon fal fa-trash-undo ml-1"></i> {t("undo")}
                                </button>
                            </div>
                            <div className="col">
                                <button type="button" className="btn btn-light btn-sm btn-block" onClick={() => {
                                    toast.update(_toastId, { render: () => <div><i className="fa-spin fas fa-spinner"></i> {t("Processing")}</div>, autoClose: false });
                                    apiCall()
                                        .then((response) => {
                                            if (!response || response.data?.code === "success" || response.status === 200) {
                                                toast.update(_toastId, {
                                                    render: () => <div><div className="h5"><i className={`icon ${successIcon}`}></i> {t(successTitleKey)}</div>{successBodyKey && <div className="h6">{t(successBodyKey)}</div>}</div>,
                                                    type: toast.TYPE.SUCCESS,
                                                    autoClose: 5000,
                                                });
                                                if (onSuccess) onSuccess();
                                            }
                                        })
                                        .catch(() => {
                                            toast.update(_toastId, { render: () => <div><div className="h5"><i className="icon fal fa-wifi-slash"></i> {t("Error.NoConnection.Title")}</div></div>, type: toast.TYPE.ERROR, autoClose: 5000 });
                                        });
                                }}>
                                    <i className="icon fal fa-recycle ml-1"></i> {t("confirm")}
                                </button>
                            </div>
                        </div>
                    </div>
                ),
                type: toast.TYPE.DARK,
                closeOnClick: false,
                pauseOnHover: true,
                draggable: true,
            });
        };

        switch (actionType) {
            case "restore":
                confirmAndExecute(
                    t("Processing"),
                    () => api.post(`/Users/${item.Id}`),
                    "fal fa-trash-undo-alt",
                    "Pages.ProfileEdit.restore_toast.Title",
                    "Pages.ProfileEdit.restore_toast.Body"
                );
                break;
            case "fire":
                confirmAndExecute(
                    t("Processing"),
                    () => api.delete(`/Users/${item.Id}?HardDelete=${true}`),
                    "fal fa-trash-undo-alt",
                    "Pages.ProfileEdit.delete_toast.Title",
                    "Pages.ProfileEdit.delete_toast.Body"
                );
                break;
            case "useAs":
                confirmAndExecute(
                    t("UserAsProcessing"),
                    () => api.post(`/UserAsUser/${item.Id}`).then(response => {
                        if (response.data.code === "success") {
                            let prevToken = localStorage.getItem("token");
                            localStorage.setItem("prevToken", prevToken);
                            localStorage.setItem("token", response.data.token);
                            dispatch(userLogin(response.data));
                            setToken(response.data.token);
                            history.push("/Profile");
                        }
                        return response;
                    }),
                    "fal fa-shield-check",
                    "Pages.Login.success_toast.Title",
                    "Pages.Login.success_toast.Body"
                );
                break;
            case "testUser":
                confirmAndExecute(
                    t("TestUserProcessing"),
                    () => api.put(`/UserAsUser/${item.Id}`),
                    "fal fa-shield-check",
                    "Pages.UsersControl.toast.test_user.Title",
                    "Pages.UsersControl.toast.test_user.Body"
                );
                break;
            case "changePermission":
                confirmAndExecute(
                    t("ChangePermissionUserProcessing"),
                    () => api.post(`/ChangeUserType/${item.Id}`, { UserTypeId: item.UserType.Id === 3 ? 4 : 3 }),
                    "fal fa-shield-check",
                    "Pages.UsersControl.toast.ChangePermission.Title",
                    "Pages.UsersControl.toast.ChangePermission.Body"
                );
                break;
            case "changeUserType":
                if (item.UserType.Id === 4) {
                    confirmAndExecute(
                        t("ChangePermissionUserProcessing"),
                        () => api.put(`/ChangeUserType/${item.Id}`, { SchoolId: 0, UserTypeId: 2 }),
                        "fal fa-shield-check",
                        "Pages.UsersControl.toast.ChangePermission.Title",
                        "Pages.UsersControl.toast.ChangePermission.Body"
                    );
                } else {
                    setInternalActiveModal("changeSchool");
                    setInternalActiveSubModal(null);
                }
                break;
            case "accessToAllClass":
                confirmAndExecute(
                    t("ChangeAccessToClassProcessing"),
                    () => api.post(`/ChangeAccessToAllClass/${item.Id}`),
                    "fal fa-shield-check",
                    "Pages.UsersControl.toast.ChangePermission.Title",
                    "Pages.UsersControl.toast.ChangePermission.Body"
                );
                break;
            case "remove":
                confirmAndExecute(
                    t("DeleteProcessing"),
                    () => api.delete(`/Users/${item.Id}`),
                    "fal fa-exclamation-triangle",
                    "Pages.UsersControl.toast.Delete.Title",
                    "Pages.UsersControl.toast.Delete.Body"
                );
                break;
            case "edit":
            case "bills":
            case "notes":
            case "change_password":
            case "changeLoginInfo":
            case "move":
            case "moreInfo":
            case "errorReports":
            case "contactMessages":
            case "downloadFiles":
            case "lessonMarks":
            case "lessonViews":
                if (item) {
                    setInternalActiveUser(item);
                }
                setInternalActiveModal(null);
                setTimeout(() => {
                    if (activeModal === "moreInfo" && actionType !== "moreInfo") {
                        setInternalActiveSubModal(actionType);
                        setInternalActiveModal("moreInfo");
                    } else {
                        setInternalActiveModal(actionType);
                        setInternalActiveSubModal(null);
                    }
                }, 100);
                break;
            default:
                break;
        }
    }, [dispatch, t, activeModal, propOnAction, onSuccess]);

    const renderUserSwitcher = () => {
        if (!activeUser || !activeUser.SubUsers || activeUser.SubUsers.length === 0) {
            return null;
        }

        const allUsers = [activeUser, ...activeUser.SubUsers];

        return (
            <div className="user-switcher-inline-wrapper">
                <i className="fad fa-user-friends text-muted" style={{ fontSize: '0.85rem' }}></i>
                <select
                    className="form-control form-control-sm user-switcher-select-field"
                    value={selectedUser?.Id || ""}
                    onChange={(e) => {
                        const val = parseInt(e.target.value, 10);
                        const matched = allUsers.find(u => u.Id === val);
                        if (matched) setSelectedUser(matched);
                    }}
                >
                    {allUsers.map((u) => (
                        <option key={u.Id} value={u.Id}>
                            {u.Name} {u.Id === activeUser.Id ? `(${t("Pages.UsersControl.relativeParent") || "ولي الأمر"})` : `(${t("Pages.UsersControl.relativeSubUser") || "تابع"})`}
                        </option>
                    ))}
                </select>
            </div>
        );
    };

    if (!activeModal || !activeUser) return null;

    const userSwitcher = renderUserSwitcher();

    return (
        <>
            {/* Bills Modal */}
            {(activeModal === "bills" || activeSubModal === "bills") && (
                <UserBill handleClose={handleClose} user={selectedUser} userSwitcher={userSwitcher} />
            )}

            {/* Notes Modal */}
            {(activeModal === "notes" || activeSubModal === "notes") && (
                <UserNote handleClose={handleClose} user={selectedUser} userSwitcher={userSwitcher} />
            )}

            {/* Change Login Info Modal */}
            <ChangeLoginInfoModal
                show={activeModal === "changeLoginInfo" || activeSubModal === "changeLoginInfo"}
                onClose={handleClose}
                user={activeUser}
                onSubmit={propSubmitLoginInfo}
                onSuccess={onSuccess}
                t={t}
            />

            {/* Change Password Modal */}
            <ChangePasswordModal
                show={activeModal === "change_password" || activeSubModal === "change_password"}
                onClose={handleClose}
                user={activeUser}
                onSubmit={propSubmitPassword}
                onSuccess={onSuccess}
                t={t}
            />

            {/* Edit Profile Modal */}
            <EditProfileModal
                show={activeModal === "edit" || activeSubModal === "edit"}
                onClose={handleClose}
                user={activeUser}
                onSubmit={propSubmitEdit}
                onSuccess={onSuccess}
                t={t}
            />

            {/* Change School Modal */}
            <ChangeSchoolModal
                show={activeModal === "changeSchool" || activeSubModal === "changeSchool"}
                onClose={handleClose}
                user={activeUser}
                onSubmit={propSubmitChangeSchool}
                promiseSchoolOptions={propPromiseSchoolOptions}
                onSuccess={onSuccess}
                t={t}
            />

            {/* Move Student Class Room Modal */}
            <MoveClassRoomModal
                show={activeModal === "move" || activeSubModal === "move"}
                onClose={handleClose}
                user={activeUser}
                onSubmit={propSubmitChangeClassRoom}
                promiseClassRoomOptions={propPromiseClassRoomOptions}
                onSuccess={onSuccess}
                t={t}
            />

            {/* More Info Modal */}
            <MoreInfoModal
                show={activeModal === "moreInfo" && !activeSubModal}
                onClose={handleClose}
                user={activeUser}
                onAction={handleActionTrigger}
                t={t}
            />

            {/* Error Reports Modal */}
            {(activeModal === "errorReports" || activeSubModal === "errorReports") && (
                <UserErrorReportsModal
                    show={true}
                    onClose={handleClose}
                    user={selectedUser}
                    userSwitcher={userSwitcher}
                    t={t}
                />
            )}

            {/* Contact Messages Modal */}
            {(activeModal === "contactMessages" || activeSubModal === "contactMessages") && (
                <UserContactMessagesModal
                    show={true}
                    onClose={handleClose}
                    user={selectedUser}
                    userSwitcher={userSwitcher}
                    t={t}
                />
            )}

            {/* Download Files Modal */}
            {(activeModal === "downloadFiles" || activeSubModal === "downloadFiles") && (
                <UserFileHistoryModal
                    show={true}
                    onClose={handleClose}
                    user={selectedUser}
                    userSwitcher={userSwitcher}
                    t={t}
                />
            )}

            {/* Lesson Marks Modal */}
            {(activeModal === "lessonMarks" || activeSubModal === "lessonMarks") && (
                <UserLessonMarksModal
                    show={true}
                    onClose={handleClose}
                    user={selectedUser}
                    userSwitcher={userSwitcher}
                    t={t}
                />
            )}

            {/* Lesson Views Modal */}
            {(activeModal === "lessonViews" || activeSubModal === "lessonViews") && (
                <UserLessonHistoryModal
                    show={true}
                    onClose={handleClose}
                    user={selectedUser}
                    userSwitcher={userSwitcher}
                    t={t}
                />
            )}

            {/* School details modal (triggered by clicking school name) */}
            {schoolModalOpen && selectedSchool && (
                <SchoolModals
                    activeModal="info"
                    selectedSchool={selectedSchool}
                    schoolUsers={schoolUsers}
                    loadingSchoolUsers={loadingSchoolUsers}
                    countries={configCountries.countries.map(item => ({
                        value: item.value,
                        name: item.label,
                        label: <div><LazyLoadImage src={process.env.PUBLIC_URL + item.icon} /> {item.label}</div>,
                        icon: item.icon
                    }))}
                    onClose={onCloseSchoolModal}
                    onAction={(actionType, adminUser) => {
                        if (actionType === "adminInfo") {
                            onCloseSchoolModal();
                            handleActionTrigger("moreInfo", adminUser);
                        }
                    }}
                    t={t}
                />
            )}
        </>
    );
};

export default UserModals;
